import toast from 'react-hot-toast';
import logger from './logger';

/**
 * Centralized error handler for API errors
 *
 * @param {Error} error - The error object
 * @param {string} context - Module/component name (e.g., 'PROFILE', 'CHAT')
 * @param {string} fallbackMessage - User-friendly message if extraction fails
 * @returns {object} - { userMessage, error, status, isNetworkError, isAuthError }
 */
export const handleApiError = (error, context, fallbackMessage = 'An error occurred') => {
  // Handle null/undefined errors
  if (!error) {
    const message = 'Network error. Please check your connection.';
    toast.error(message);
    logger.error(context, 'API error', { message: 'No error object provided' });
    return {
      userMessage: message,
      error: null,
      status: undefined,
      isNetworkError: true,
      isAuthError: false
    };
  }

  // 1. Extract clean user message
  const userMessage = extractUserMessage(error, fallbackMessage);

  // 2. Show to user
  toast.error(userMessage);

  // 3. Log for debugging (structured)
  logger.error(context, 'API error', {
    message: error.message || 'Unknown error',
    status: error.response?.status,
    endpoint: error.config?.url,
    method: error.config?.method,
    data: error.response?.data
  });

  // 4. Return for component-specific handling if needed
  return {
    userMessage,
    error,
    status: error.response?.status,
    isNetworkError: !error.response,
    isAuthError: error.response?.status === 401 || error.response?.status === 403
  };
};

/**
 * Extract user-friendly message from error
 */
const extractUserMessage = (error, fallback) => {
  // Handle null/undefined errors
  if (!error) {
    return 'Network error. Please check your connection.';
  }

  // Network error (no response)
  if (!error.response) {
    return 'Network error. Please check your connection.';
  }

  // Try multiple response formats (backend uses different formats)
  const data = error.response?.data;

  // Format 1: { original_detail: { message: '...' } }
  if (data?.original_detail?.message) {
    const sanitized = sanitizeMessage(data.original_detail.message);
    if (sanitized) return sanitized;
  }

  // Format 2: { detail: { message: '...' } }
  if (data?.detail?.message) {
    const sanitized = sanitizeMessage(data.detail.message);
    if (sanitized) return sanitized;
  }

  // Format 3: { detail: 'string message' }
  if (typeof data?.detail === 'string') {
    // If empty string, use fallback immediately
    if (data.detail.length === 0) {
      return fallback;
    }
    const sanitized = sanitizeMessage(data.detail);
    if (sanitized) return sanitized;
    // If sanitization returned null (empty/short), use fallback
    return fallback;
  }

  // Format 4: { detail: [ { msg: '...' } ] } (FastAPI validation)
  if (Array.isArray(data?.detail)) {
    const messages = data.detail.map(e => e.msg || e.message).filter(Boolean);
    return messages.length > 0 ? messages.join(', ') : fallback;
  }

  // Format 5: { message: '...' }
  if (data?.message) {
    const sanitized = sanitizeMessage(data.message);
    if (sanitized) return sanitized;
    // If sanitization returned null (empty/short), use fallback
    return fallback;
  }

  // Format 6: { error: '...' }
  if (data?.error && typeof data.error === 'string') {
    const sanitized = sanitizeMessage(data.error);
    if (sanitized) return sanitized;
    // If sanitization returned null (empty/short), use fallback
    return fallback;
  }

  // HTTP status messages (only use if no message was found above)
  const statusMessages = {
    400: 'Invalid request. Please check your input.',
    401: 'Authentication required. Please log in again.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested resource was not found.',
    409: 'This action conflicts with existing data.',
    422: 'Invalid data provided. Please check your input.',
    429: 'Too many requests. Please try again later.',
    500: 'Server error. Please try again later.',
    502: 'Service temporarily unavailable. Please try again.',
    503: 'Service temporarily unavailable. Please try again.'
  };

  const status = error.response?.status;
  if (statusMessages[status]) {
    return statusMessages[status];
  }

  // Fallback
  return fallback;
};

/**
 * Remove technical details from error messages
 * IMPORTANT: This function should remove dangerous technical errors
 * while preserving legitimate user-facing validation messages
 */
const sanitizeMessage = (message) => {
  // Return null for empty messages so fallback can be used
  if (!message || typeof message !== 'string' || message.trim() === '') {
    return null;
  }

  // Remove SQL errors (dangerous - always hide these)
  if (message.includes('SQL') || message.includes('database') || message.includes('psycopg')) {
    return 'A database error occurred. Please try again.';
  }

  // Remove stack traces (dangerous - always hide these)
  // Look for stack trace patterns, not just any "at"
  const hasStackTrace = message.includes('Traceback') ||
                        message.includes('File "') ||
                        message.match(/\n\s+at\s+/) ||  // "\n  at " (typical stack trace)
                        message.match(/^\s+at\s+/);     // "  at " at start

  if (hasStackTrace) {
    return 'An internal error occurred. Please try again.';
  }

  // Remove file paths
  message = message.replace(/\/[^\s]+\.(py|js|ts)/g, '');

  // IMPROVED: Only remove specific technical error codes (whitelist approach)
  // This preserves legitimate uppercase words like API, CSV, PNG, JSON, URL, PDF, etc.
  const technicalErrorCodes = [
    'ECONNREFUSED', 'ETIMEDOUT', 'ENOTFOUND', 'EADDRINUSE', 'EACCES', 'EPIPE',
    'ERR_NETWORK', 'ERR_CONNECTION', 'ERR_TIMEOUT', 'ERR_CANCELED', 'ERR_BAD_REQUEST',
    'EHOSTUNREACH', 'ENETUNREACH', 'ECONNRESET', 'EPERM'
  ];

  technicalErrorCodes.forEach(code => {
    const regex = new RegExp(`\\b${code}\\b`, 'g');
    message = message.replace(regex, '');
  });

  // Remove extra whitespace
  message = message.trim().replace(/\s+/g, ' ');

  // If message is too short after sanitization, return null so fallback is used
  if (message.length < 5) {
    return null;
  }

  return message;
};

/**
 * Handle form validation errors
 */
export const handleValidationError = (error, context) => {
  const data = error.response?.data;

  // FastAPI validation format
  if (Array.isArray(data?.detail)) {
    const errors = data.detail.map(err => {
      const field = err.loc?.[1] || 'field';
      return `${field}: ${err.msg}`;
    });

    errors.forEach(msg => toast.error(msg));
    logger.warn(context, 'Validation errors', { errors: data.detail });

    return { errors: data.detail, isValidation: true };
  }

  // Fallback to standard handler
  return handleApiError(error, context, 'Validation failed');
};

/**
 * Handle auth errors (logout user if needed)
 */
export const handleAuthError = (error, context, onLogout) => {
  const result = handleApiError(error, context, 'Authentication failed');

  if (result.isAuthError && onLogout) {
    // Give user time to read error message
    setTimeout(() => {
      onLogout();
    }, 2000);
  }

  return result;
};

/**
 * Extract error message for display (legacy compatibility)
 * @deprecated Use handleApiError instead
 */
export const extractErrorMessage = (error, fallback = 'An error occurred') => {
  return extractUserMessage(error, fallback);
};

const errorHandler = {
  handleApiError,
  handleValidationError,
  handleAuthError,
  extractErrorMessage
};

export default errorHandler;
